/**
 * LinkTimePage — Manage Meeting Rooms and Meetings
 * 
 * Model:
 * - Meeting Room: persistent container (e.g., "Project Alpha")
 * - Meeting: one occasion in a room, has ONE invite link shared by all guests
 */

import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../auth';
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer
} from '@livekit/components-react';
import '@livekit/components-styles';
import './LinkTimePage.css';

const LinkTimePage = ({ user }) => {
  const [rooms, setRooms] = useState([]);
  const [meetingsByRoom, setMeetingsByRoom] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [showCreateMeetingModal, setShowCreateMeetingModal] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  
  const [inCall, setInCall] = useState(false);
  const [callData, setCallData] = useState(null);

  const loadRooms = useCallback(async () => {
    try {
      const res = await authFetch('/api/linktime/rooms');
      if (!res.ok) throw new Error('Failed to load rooms');
      const data = await res.json();
      setRooms(data.rooms || []);
      
      const meetingsPromises = (data.rooms || []).map(async (room) => {
        const mRes = await authFetch(`/api/linktime/rooms/${room.room_id}/meetings`);
        if (mRes.ok) {
          const mData = await mRes.json();
          return { roomId: room.room_id, meetings: mData.meetings || [] };
        }
        return { roomId: room.room_id, meetings: [] };
      });
      
      const meetingsResults = await Promise.all(meetingsPromises);
      const meetingsMap = {};
      meetingsResults.forEach(({ roomId, meetings }) => {
        meetingsMap[roomId] = meetings;
      });
      setMeetingsByRoom(meetingsMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const handleCreateRoom = async (data) => {
    setActionMessage({ type: 'success', text: data.suggested ? 'Room created as /' + data.alias : 'Room created!' });
    setShowCreateRoomModal(false);
    loadRooms();
  };

  const handleCreateMeeting = async (roomId, formData) => {
    try {
      const res = await authFetch(`/api/linktime/rooms/${roomId}/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create meeting');
      setActionMessage({ type: 'success', text: 'Meeting created!' });
      setShowCreateMeetingModal(null);
      loadRooms();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Delete this room and ALL its meetings?')) return;
    try {
      const res = await authFetch(`/api/linktime/rooms/${roomId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete room');
      setActionMessage({ type: 'success', text: 'Room deleted' });
      if (selectedRoomId === roomId) setSelectedRoomId(null);
      loadRooms();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    }
  };

  const handleDeleteMeeting = async (roomId, meetingId) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      const res = await authFetch(`/api/linktime/rooms/${roomId}/meetings/${meetingId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete meeting');
      setActionMessage({ type: 'success', text: 'Meeting deleted' });
      loadRooms();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    }
  };

  const handleJoinMeeting = useCallback(async (meetingId, roomAlias, meetingCode) => {
    try {
      // If alias or code missing (e.g. from auto-join URL), we need to get them from the API first
      let targetAlias = roomAlias;
      let targetCode = meetingCode;

      if (!targetAlias || !targetCode) {
        const infoRes = await authFetch(`/api/linktime/meetings/${meetingId}`);
        if (infoRes.ok) {
          const infoData = await infoRes.json();
          targetAlias = infoData.room_alias;
          targetCode = infoData.meeting_code;
        } else {
          throw new Error('Could not resolve meeting details');
        }
      }

      const res = await authFetch(`/api/linktime/meetings/${meetingId}/host-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: user?.email?.split('@')[0] || 'Host' })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to join');
      }
      const data = await res.json();
      const hostToken = data.token;
      const hostName = user?.email?.split('@')[0] || 'Host';
      const wsUrl = data.wsUrl;
      const room = data.room;
      const joinUrl = `/link/${targetAlias}/${targetCode}?hostToken=${encodeURIComponent(hostToken)}&hostName=${encodeURIComponent(hostName)}&wsUrl=${encodeURIComponent(wsUrl)}${room ? `&room=${encodeURIComponent(room)}` : ''}`;
      window.location.assign(joinUrl);
      return true;
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
      return false;
    }
  }, [user?.email]);

  useEffect(() => {
    if (loading) return;

    const params = new URLSearchParams(window.location.search);
    const meetingId = params.get('join');
    if (meetingId) {
      (async () => {
        // Find the meeting in loaded state when available; otherwise the API can resolve it.
        let foundAlias, foundCode;
        for (const roomId in meetingsByRoom) {
          const meeting = meetingsByRoom[roomId].find(m => m.meeting_id === meetingId);
          if (meeting) {
            const room = rooms.find(r => r.room_id === roomId);
            foundAlias = room?.alias;
            foundCode = meeting.meeting_code;
            break;
          }
        }
        const joined = await handleJoinMeeting(meetingId, foundAlias, foundCode);
        if (joined) window.history.replaceState({}, '', window.location.pathname);
      })();
    }
  }, [handleJoinMeeting, loading, meetingsByRoom, rooms]);

  const copyToClipboard = (text, label = 'Link') => {
    navigator.clipboard.writeText(text).then(() => {
      setActionMessage({ type: 'success', text: `${label} copied!` });
    });
  };

  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  // Redundant in-page call logic removed as host now joins via a new window (LinkTimeJoinPage)
  // handleJoinMeeting opens the link with hostToken params

  return (
    <div className="linktime-page">
      <div className="linktime-header">
        <div>
          <h1>LinkTime</h1>
          <p className="linktime-subtitle">Meeting Rooms → Meetings → Guests</p>
        </div>
        <button className="linktime-create-btn" onClick={() => setShowCreateRoomModal(true)}>
          + Create Room
        </button>
      </div>

      <section className="linktime-explainer">
        <div className="linktime-explainer-intro">
          <h2>Think of LinkTime as a digital open door.</h2>
          <p>
            Right now, if someone wants to talk to you, they usually have to book a formal slot on your calendar
            or call out of the blue. LinkTime keeps the speed of a call, without the interruption.
          </p>
        </div>
        <div className="linktime-explainer-grid">
          <div className="linktime-explainer-item">
            <strong>Your Personal Room</strong>
            <p>You keep one permanent meeting link ready to send to a friend, client, or family member.</p>
          </div>
          <div className="linktime-explainer-item">
            <strong>The Virtual Knock</strong>
            <p>They click the link whenever they are ready. If you are not already there, they can knock instead of booking a formal appointment.</p>
          </div>
          <div className="linktime-explainer-item">
            <strong>Real-Time Reply</strong>
            <p>You get the notification and can answer right away or send back a quick “give me 5 minutes.”</p>
          </div>
          <div className="linktime-explainer-item">
            <strong>No Pressure</strong>
            <p>It feels more like seeing someone at the kitchen table than getting trapped in phone tag or waiting days for a scheduled slot.</p>
          </div>
        </div>
        <div className="linktime-explainer-footer">
          <p>
            Only people you gave the link to can knock, and you answer when you actually have the moment to spare.
          </p>
          <p className="linktime-explainer-summary">
            In short: it is the speed of a phone call with the politeness of a scheduled meeting.
          </p>
        </div>
      </section>

      {actionMessage && (
        <div className={`linktime-message ${actionMessage.type}`}>
          {actionMessage.text}
        </div>
      )}

      {loading ? (
        <div className="linktime-loading">Loading...</div>
      ) : error ? (
        <div className="linktime-error">{error}</div>
      ) : rooms.length === 0 ? (
        <div className="linktime-empty">
          <div className="linktime-empty-icon">🏠</div>
          <h3>No Meeting Rooms yet</h3>
          <p>Create a room, then add meetings to it.</p>
          <button className="linktime-create-btn" onClick={() => setShowCreateRoomModal(true)}>
            Create Your First Room
          </button>
        </div>
      ) : (
        <div className="linktime-rooms-list">
          {rooms.map(room => (
            <RoomSection
              key={room.room_id}
              room={room}
              meetings={meetingsByRoom[room.room_id] || []}
              isExpanded={selectedRoomId === room.room_id}
              onToggle={() => setSelectedRoomId(selectedRoomId === room.room_id ? null : room.room_id)}
              onDelete={() => handleDeleteRoom(room.room_id)}
              onCreateMeeting={() => setShowCreateMeetingModal(room.room_id)}
              onDeleteMeeting={(mid) => handleDeleteMeeting(room.room_id, mid)}
              onJoinMeeting={(mid, alias, code) => handleJoinMeeting(mid, alias, code)}
              onCopy={copyToClipboard}
            />
          ))}
        </div>
      )}

      {showCreateRoomModal && (
        <CreateRoomModal
          user={user}
          onClose={() => setShowCreateRoomModal(false)}
          onCreate={handleCreateRoom}
        />
      )}

      {showCreateMeetingModal && (
        <CreateMeetingModal
          roomId={showCreateMeetingModal}
          onClose={() => setShowCreateMeetingModal(null)}
          onCreate={(formData) => handleCreateMeeting(showCreateMeetingModal, formData)}
        />
      )}
    </div>
  );
};

const RoomSection = ({ room, meetings, isExpanded, onToggle, onDelete, onCreateMeeting, onDeleteMeeting, onJoinMeeting, onCopy }) => {
  const isExpired = (room) => false;

  return (
    <div className={`linktime-room-section ${isExpanded ? 'expanded' : ''}`}>
      <div className="linktime-room-header" onClick={onToggle}>
        <div className="linktime-room-info">
          <h3>{room.name}</h3>
          <span className="linktime-room-alias">alias: {room.alias}</span>
        </div>
        <div className="linktime-room-meta">
          <span className="linktime-meeting-count">{meetings.length} meeting{meetings.length !== 1 ? 's' : ''}</span>
          <span className="linktime-expand-icon">{isExpanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {isExpanded && (
        <div className="linktime-room-details">
          <div className="linktime-room-actions">
            <button className="linktime-create-meeting-btn" onClick={onCreateMeeting}>
              + New Meeting
            </button>
            <button className="linktime-delete-btn" onClick={onDelete}>
              Delete Room
            </button>
          </div>

          {meetings.length === 0 ? (
            <p className="linktime-no-meetings">No meetings yet. Create one to get an invite link!</p>
          ) : (
            <div className="linktime-meetings-list">
              {meetings.map(meeting => (
                <div key={meeting.meeting_id} className="linktime-meeting-card">
                  <div className="linktime-meeting-info">
                    <h4>{meeting.name}</h4>
                    <code className="linktime-meeting-code">{meeting.meeting_code}</code>
                    <span className="linktime-meeting-stats">
                      {meeting.guest_count || 0} guest{meeting.guest_count !== 1 ? 's' : ''} joined
                    </span>
                  </div>
                  <div className="linktime-meeting-actions">
                    <button
                      className="linktime-copy-btn variant-guest"
                      onClick={(e) => {
                        navigator.clipboard.writeText(meeting.app_share_url + '?force_guest=1');
                        var b = e.currentTarget; b.textContent = '✓ Copied'; setTimeout(function() { b.textContent = '📋 Guest'; }, 2000);
                      }}
                      title="Copy guest invite link"
                    >
                      📋 Guest
                    </button>
                    <button
                      className="linktime-copy-btn variant-host"
                      onClick={(e) => { navigator.clipboard.writeText(window.location.origin + '/linktime?join=' + meeting.meeting_id); var b = e.currentTarget; b.textContent = '✓ Copied'; setTimeout(function() { b.textContent = '🔑 Host'; }, 2000); }}
                      title="Copy host dashboard link"
                    >
                      🔑 Host
                    </button>
                    <div className="linktime-split-btn">
                      <button
                        className="linktime-join-btn"
                        onClick={() => onJoinMeeting(meeting.meeting_id, room.alias, meeting.meeting_code)}
                      >
                        Join as Host
                      </button>
                      <button
                        className="linktime-split-dropdown"
                        onClick={(e) => { var dd = e.currentTarget.nextElementSibling; dd.style.display = dd.style.display === 'block' ? 'none' : 'block'; }}
                      >
                        ▾
                      </button>
                      <div className="linktime-split-dropdown-menu" style={{display:'none'}}>
                        <button onClick={() => { window.open(meeting.app_share_url, '_blank'); }}>
                          👤 Join as Guest
                        </button>
                      </div>
                    </div>
                    <button
                      className="linktime-delete-btn small"
                      onClick={() => onDeleteMeeting(meeting.meeting_id)}
                      title="Delete meeting"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CreateRoomModal = ({ user, onClose, onCreate }) => {
  const defaultAlias = (user?.email?.split('@')[0] || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
  const [name, setName] = useState('');
  const [alias, setAlias] = useState(defaultAlias);
  const [checking, setChecking] = useState(!!defaultAlias);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Check availability of default alias when modal opens
  useEffect(function() {
    if (!defaultAlias) { setChecking(false); return; }
    authFetch('/api/linktime/rooms/check-alias/' + defaultAlias)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (!data.available) {
          var next = data.suggestion || (defaultAlias + '-2');
          setAlias(next);
        }
        setChecking(false);
      })
      .catch(function() { setChecking(false); });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    var cleaned = alias.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    try {
      const res = await authFetch('/api/linktime/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name || 'My Meeting Room',
          alias: cleaned && cleaned !== defaultAlias ? cleaned : undefined
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create room');
        setSubmitting(false);
        return;
      }
      onCreate(data);
    } catch (err) {
      setError(err.message || 'Failed to create room');
      setSubmitting(false);
    }
  };

  return (
    <div className="linktime-modal-overlay" onClick={onClose}>
      <div className="linktime-modal" onClick={(e) => e.stopPropagation()}>
        <div className="linktime-modal-header">
          <h2>Create Meeting Room</h2>
          <button className="linktime-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="linktime-form-group">
            <label>Room Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Project Alpha, Weekly Standup"
            />
          </div>

          <div className="linktime-form-group">
            <label>Room Alias (URL prefix)</label>
            <div className="linktime-alias-preview">
              <span>link.numfree.com/</span>
              <input
                type="text"
                value={alias}
                onChange={(e) => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="yourname"
                required
                minLength={3}
              />
              <span>/...</span>
            </div>
            <small>Your room's URL prefix</small>
          </div>

          {error && <div className="linktime-modal-error">{error}</div>}

          <div className="linktime-modal-actions">
            <button type="button" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="linktime-primary-btn" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateMeetingModal = ({ roomId, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onCreate({
      name: name || 'Meeting',
      password: password || null,
      expires_in_days: expiresInDays ? parseInt(expiresInDays, 10) : null
    });
    setSubmitting(false);
  };

  return (
    <div className="linktime-modal-overlay" onClick={onClose}>
      <div className="linktime-modal" onClick={(e) => e.stopPropagation()}>
        <div className="linktime-modal-header">
          <h2>Create Meeting</h2>
          <button className="linktime-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="linktime-form-group">
            <label>Meeting Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Kickoff Call, Interview with John"
            />
          </div>

          <div className="linktime-form-group">
            <label>Password (optional)</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave empty for no password"
            />
          </div>

          <div className="linktime-form-group">
            <label>Expires After</label>
            <select value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)}>
              <option value="">Never</option>
              <option value="1">1 day</option>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
            </select>
          </div>

          <div className="linktime-modal-actions">
            <button type="button" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="linktime-primary-btn" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Meeting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LinkTimePage;
